import React, { useState, useRef } from 'react';
import { X, Upload, Image as ImageIcon, Loader2, Check } from 'lucide-react';
import { useMediaLibrary, useUploadMedia } from './hooks';

interface MediaGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
}

export const MediaGalleryModal: React.FC<MediaGalleryModalProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const { data: mediaFiles = [], isLoading: isLoadingMedia } = useMediaLibrary();
  const { mutate: uploadMedia, isPending: isUploading } = useUploadMedia();
  
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMedia(file, {
        onSuccess: (url) => {
          setSelectedUrl(url);
        },
        onError: (error: any) => {
          alert(error.message || 'Lỗi khi tải ảnh lên.');
        }
      });
    }
  };

  const handleConfirm = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-slate-700" />
            <h3 className="text-lg font-bold text-slate-800">Thư viện ảnh câu hỏi</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Top Bar */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Chọn ảnh đã tải lên hoặc tải ảnh mới từ máy tính của bạn.
          </p>
          
          <div>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept="image/*" 
              className="hidden" 
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {isUploading ? 'Đang tải lên...' : 'Tải ảnh lên'}
            </button>
          </div>
        </div>

        {/* Body / Grid */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {isLoadingMedia ? (
            <div className="flex flex-col items-center justify-center h-48 space-y-3">
              <Loader2 className="w-8 h-8 text-[#236539] animate-spin" />
              <p className="text-sm text-slate-500">Đang tải thư viện ảnh...</p>
            </div>
          ) : mediaFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 space-y-3 text-slate-400">
              <ImageIcon className="w-12 h-12 opacity-50" />
              <p>Chưa có ảnh nào trong thư viện.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
              {mediaFiles.map((file: any) => (
                <div 
                  key={file.url}
                  onClick={() => setSelectedUrl(file.url)}
                  className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer group border-2 transition-all ${
                    selectedUrl === file.url 
                      ? 'border-[#236539] ring-2 ring-[#236539]/20' 
                      : 'border-transparent hover:border-slate-300'
                  }`}
                >
                  <img 
                    src={file.url} 
                    alt={file.name || 'Ảnh câu hỏi'} 
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Selection Overlay */}
                  {selectedUrl === file.url && (
                    <div className="absolute inset-0 bg-[#236539]/10 flex items-center justify-center">
                      <div className="bg-[#236539] text-white p-1.5 rounded-full shadow-sm">
                        <Check className="w-4 h-4" />
                      </div>
                    </div>
                  )}
                  
                  {/* Hover Overlay */}
                  {selectedUrl !== file.url && (
                    <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-white">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-slate-600 font-medium hover:bg-slate-100 rounded-xl transition-colors"
          >
            Hủy bỏ
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedUrl || isUploading}
            className="px-5 py-2.5 bg-[#236539] text-white font-medium rounded-xl hover:bg-[#1a4a2a] disabled:opacity-50 disabled:hover:bg-[#236539] transition-colors"
          >
            Xác nhận chọn ảnh
          </button>
        </div>
      </div>
    </div>
  );
};
